import * as React from 'react';
import { connect } from 'react-redux';

import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';

import InspirationalMaterialsCampaignContentFilters from './filters/InspirationalMaterialsCampaignContentFilters';
import InspirationalMaterialsGrid from '../inspirational-materials-grid/InspirationalMaterialsGrid';

import {
  fetchCampaignAssets,
  resetAssets
} from 'modules/campaigns-inspirational-materials/reducers/inspirationalCampaignAssets';
import { resetSearchCampaigns, searchCampaigns } from 'modules/admin/reducers/admin-search-campaigns';
import { addImage } from '../../reducers/inspirationalMaterials';

import { getBrandProfileId } from 'utils/organization';
import { __t, applyTranslations } from 'utils/language';

import type { APISimpleDropDownProfileResponse, DeliverableAttachment, ImageStandard } from 'types';
import type { ConsentReducerConsentsMap } from 'redux-store/types/ConsentReducer.type';

import './inspirational-materials-campaign-content.scss';

applyTranslations(require('./inspirational-materials-campaign-content.i18n.json'));

type StateProps = {
  campaignList: Array<APISimpleDropDownProfileResponse>,
  brandId: number | undefined | null,
  campaignAssets: Array<DeliverableAttachment>,
  hasMorePages: boolean,
  currentPage: number,
  isLoading: boolean,
  userChannelConsents: ConsentReducerConsentsMap
};

type DispatchProps = {
  dispatchAddImage: (a: ImageStandard) => void,
  dispatchSearchCampaigns: (a: number) => void,
  dispatchResetCampaigns: () => void,
  dispatchFetchContent: (a: number, page?: number) => void,
  dispatchResetContent: () => void
};

type Props = StateProps & DispatchProps;

function mapCampaignAssetToImage(asset: DeliverableAttachment): ImageStandard {
  return {
    assetUrl: asset.url,
    code: String(asset.id),
    type: 'cloudinary-image',
    url: asset.url
  };
}

const InspirationalMaterialsCampaignContent = (
  {
    campaignList,
    brandId,
    campaignAssets,
    isLoading,
    hasMorePages,
    currentPage,
    userChannelConsents,
    dispatchAddImage,
    dispatchSearchCampaigns,
    dispatchResetCampaigns,
    dispatchFetchContent,
    dispatchResetContent
  }: Props
): JSX.Element => {
  const [campaignId, setCampaignId] = React.useState<number | undefined | null>(0);

  // campaign list for dropdown
  React.useEffect(() => {
    if (brandId) {
      dispatchSearchCampaigns(brandId);
    }

    return () => {
      dispatchResetCampaigns();
    };
  }, [brandId]);

  // campaign assets
  React.useEffect(() => {
    if (campaignId) {
      dispatchFetchContent(campaignId);
    }

    return () => {
      dispatchResetContent();
    };
  }, [campaignId]);

  const hasContent = isLoading || campaignAssets.length > 0;

  return (
    <section className="inspirational-materials-campaign-content">
      <section className="inspirational-materials-campaign-content__filters">
        <InspirationalMaterialsCampaignContentFilters
          currentCampaignId={campaignId}
          campaigns={campaignList}
          onChange={setCampaignId}
        />
      </section>
      {
        !campaignId && (
          <section className="inspirational-materials-campaign-content__empty">
            {__t('inspirational_materials_campaign_content.initial_message')}
          </section>
        )
      }
      {
        Boolean(campaignId && !hasContent) && (
          <section className="inspirational-materials-campaign-content__empty">
            {__t('inspirational_materials_campaign_content.empty_message')}
          </section>
        )
      }
      {
        Boolean(campaignId && hasContent) && (
          <section className="inspirational-materials-campaign-content__grid">
            <InspirationalMaterialsGrid
              withCloudinary
              userChannelConsents={userChannelConsents}
              assets={campaignAssets.map(mapCampaignAssetToImage)}
              onSelect={dispatchAddImage}
              isLoading={isLoading}
              hasMorePages={hasMorePages}
              onLoadMore={() => {
                if (campaignId) {
                  dispatchFetchContent(campaignId, currentPage + 1);
                }
              }}
            />
            <VerticalSpacing type="small" />
          </section>
        )
      }
    </section>
  );
};

const mapStateToProps = ({ adminSearchCampaigns, inspirationalCampaignAssets, organizations, consentReducer }): StateProps => ({
  campaignList: adminSearchCampaigns.list,
  brandId: getBrandProfileId(organizations.currentOrganization),
  campaignAssets: inspirationalCampaignAssets.content,
  hasMorePages: inspirationalCampaignAssets.hasMorePages,
  currentPage: inspirationalCampaignAssets.currentPage,
  isLoading: inspirationalCampaignAssets.isLoading,
  userChannelConsents: consentReducer.consents
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchAddImage: (image: ImageStandard) => dispatch(addImage(image)),
  dispatchSearchCampaigns: (brandId: number) => dispatch(searchCampaigns(brandId)),
  dispatchResetCampaigns: () => dispatch(resetSearchCampaigns()),
  dispatchFetchContent: (campaignId: number, page?: number) => dispatch(fetchCampaignAssets(campaignId, page)),
  dispatchResetContent: () => dispatch(resetAssets())
});

export default connect(mapStateToProps, mapDispatchToProps)(InspirationalMaterialsCampaignContent);
